import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, BookmarkPlus, Video, VideoIcon } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import VideoCard from '../components/VideoCard';
import LoadingState from '../components/LoadingState';
import ErrorMessage from '../components/ErrorMessage';
import FilterBar from '../components/FilterBar';
import VideoPlayer from '../components/VideoPlayer';
import { searchYouTubeVideos, YouTubeVideo } from '../services/youtube';
import { useLocation } from 'react-router-dom';
import { getSuggestions } from '../services/dictionary';

export const Videos = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const { state } = useLocation();

  const fetchVideos = useCallback(async (term: string) => {
    if (!term.trim()) {
      setVideos([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await searchYouTubeVideos(term);
    
    setIsLoading(false);
    setVideos(result.videos);
    setError(result.error);
    setIsSearching(false);
  }, [activeFilter]);

  useEffect(() => {
    if (searchTerm) {
      const debounceTimer = setTimeout(() => fetchVideos(searchTerm), 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, fetchVideos]);

  const handleSearch = useCallback((term: string) => {
    setIsSearching(true);
    setSearchTerm(term);
    if (term.trim()) {
      fetchVideos(term);
    }
  }, [fetchVideos]);

  const handleInputChange = async (word: string) => {
    if (word.length > 1) {
      const newSuggestions = await getSuggestions(word);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (word: string) => {
    handleSearch(word);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <header className="text-center mb-12 pt-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <VideoIcon className="w-12 h-12 text-purple-600" />
                <h1 className="text-3xl text-left md:text-4xl font-bold text-gray-900">Videos Lesson</h1>
              </div>
              <p className="text-lg text-gray-600 mx-auto">
              Search any English word to find explanation videos or example of videos using the word!
              </p>
          </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto mb-12">
          <SearchBar 
            onSearch={handleSearch} 
            isLoading={isSearching}
            suggestions={suggestions}
            onSuggestionSelect={handleSuggestionSelect}
            onInputChange={handleInputChange}
            wordParam={state?.word}
            />
        </div>

        {/* {searchTerm && (
          <div className="mb-8">
            <FilterBar activeFilter={activeFilter} onFilterChange={setActiveFilter} />
          </div>
        )} */}

        <div className="mt-8">
          {error && <ErrorMessage message={error} />}
          
          {isLoading ? (
            <LoadingState />
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <VideoCard 
                  key={video.id} 
                  video={video} 
                  onPlay={setSelectedVideo}
                />
              ))}
            </div>
          ) : searchTerm && !error ? (
            <div className="text-center text-gray-600">
              No videos found for "{searchTerm}"
            </div>
          ) : null}
        </div>
      </main>

      {selectedVideo && (
        <VideoPlayer 
          videoId={selectedVideo} 
          onClose={() => setSelectedVideo(null)} 
        />
      )}
    </div>
  );
}